/**
 * Composable for managing parent material relationships in SDS Section 3
 * Handles parent resolution, validation, and UI state management
 */

import { computed, Ref } from 'vue';
import { SdsSection3Material } from '@/interfaces/model/general/sds';

/**
 * Parent material info for display purposes
 */
export interface ParentMaterialInfo {
  hasParent: boolean;
  parentMaterialName: string;
  isParentDeleted: boolean;
  parentMaterial?: SdsSection3Material;
}

/**
 * Available parent option for dropdowns
 */
export interface ParentMaterialOption {
  id: number | string;
  material_name: string;
  isNew: boolean;
  isDeleted: boolean;
}

/**
 * Composable for parent material management
 * @param materials - Reactive reference to the materials list
 */
export const useParentMaterials = (materials: Ref<SdsSection3Material[]>) => {
  /**
   * Get parent material information for a given material
   * Used for display purposes in tables and forms
   */
  const getParentMaterialInfo = (
    material: SdsSection3Material
  ): ParentMaterialInfo => {
    let parentMaterial: SdsSection3Material | undefined = undefined;

    // Check for existing parent first
    if (material.parent_material_id) {
      parentMaterial = materials.value.find(
        (m) => m.id === material.parent_material_id
      );
    }
    // Then check for temp parent (new material scenario)
    else if (material.temp_parent_material_id) {
      parentMaterial = materials.value.find(
        (m) => m.temp_material_id === material.temp_parent_material_id
      );
    }

    return {
      hasParent: !!parentMaterial,
      parentMaterialName: parentMaterial?.material_name || '',
      isParentDeleted: !!parentMaterial?.deleted_at,
      parentMaterial,
    };
  };

  /**
   * Get available parent materials for dropdown selection
   * Excludes the current material and its descendants to prevent circular references
   */
  const getAvailableParents = (
    currentMaterial: SdsSection3Material
  ): ParentMaterialOption[] => {
    return materials.value
      .filter((material) => {
        // Exclude self
        // For existing materials (id > 0), compare by ID
        if (
          material.id &&
          currentMaterial.id &&
          material.id === currentMaterial.id
        ) {
          return false;
        }

        // For new materials, compare by temp_material_id
        if (
          material.temp_material_id &&
          currentMaterial.temp_material_id &&
          material.temp_material_id === currentMaterial.temp_material_id
        ) {
          return false;
        }

        // Additional safety: if same material name and both are new (id === 0)
        if (
          material.id === 0 &&
          currentMaterial.id === 0 &&
          material.material_name === currentMaterial.material_name &&
          material.material_name
        ) {
          return false;
        }

        // Exclude deleted materials
        if (material.deleted_at) return false;

        // Exclude descendants to prevent circular references
        if (isDescendantOf(currentMaterial, material)) return false;

        return true;
      })
      .map((material) => ({
        id: material.id || (material.temp_material_id as string),
        material_name: material.material_name,
        isNew: material.id === 0,
        isDeleted: !!material.deleted_at,
      }))
      .sort((a, b) => {
        // Sort: existing materials first, then new materials, alphabetically within each group
        if (a.isNew && !b.isNew) return 1;
        if (!a.isNew && b.isNew) return -1;
        return a.material_name.localeCompare(b.material_name);
      });
  };

  /**
   * Check if a material is a descendant of another material
   * Used to prevent circular references in parent-child relationships
   */
  const isDescendantOf = (
    potentialParent: SdsSection3Material,
    child: SdsSection3Material
  ): boolean => {
    const visited = new Set<number | string>();

    const checkCircular = (current: SdsSection3Material): boolean => {
      // Create unique identifier for the material
      const currentId =
        current.id || (current.temp_material_id as string | number);

      if (visited.has(currentId)) {
        return true; // Circular reference detected
      }
      visited.add(currentId);

      // Check if current material's parent is the potential parent
      const parentId =
        current.parent_material_id || current.temp_parent_material_id;
      if (!parentId) return false;

      // If parent matches our potential parent, it's a descendant
      const potentialParentId =
        potentialParent.id ||
        (potentialParent.temp_material_id as string | number);

      if (parentId === potentialParentId) return true;

      // Recursively check parent's parent
      const parentMaterial = materials.value.find(
        (m) => (m.id && m.id === parentId) || m.temp_material_id === parentId
      );

      if (parentMaterial) {
        return checkCircular(parentMaterial);
      }

      return false;
    };

    return checkCircular(child);
  };

  /**
   * Enrich materials with parent display information
   * Adds computed properties for UI display
   */
  const enrichedMaterials = computed(() => {
    return materials.value.map((material) => {
      const parentInfo = getParentMaterialInfo(material);
      return {
        ...material,
        hasParent: parentInfo.hasParent,
        parentMaterialName: parentInfo.parentMaterialName,
        isParentDeleted: parentInfo.isParentDeleted,
      };
    });
  });

  return {
    // Core functions
    getParentMaterialInfo,
    getAvailableParents,
    isDescendantOf,

    // Computed properties
    enrichedMaterials,
  };
};
